/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import { Select, Layout, Row, Form, Col, Divider, Tooltip, message } from 'antd';
import { CaretDownOutlined } from '@ant-design/icons';
import UploadFontIcon from 'components/Icons/UploadFontIcon';
import UploadInfoIcon from 'components/Icons/UploadInfoIcon';
import { useReactiveVar, useMutation } from '@apollo/client';
import Uploader from 'components/Uploader';
import PropTypes from 'prop-types';
import _ from 'lodash';
import opentype from 'opentype.js';
import ColorBox from 'components/ColorBox';
import ResetItem from '../ResetItem/ResetItem';
import { USER_UPDATE } from 'graphql/mutations/userMutations';
import {
  fontListEnglish,
  fontListHebrew,
  fontSizes,
  defaultHebrewTitleSettings,
  defaultTitleSettings,
  defaultSubTitleSettings,
  defaultHebrewBodySettings,
  defaultBodySettings,
  numberWords,
} from 'pages/Proposal/constants/constants';
import { fontLoadedVar, userVar } from 'graphql/cache';
import { useProposalContext } from 'context/proposal';
import { loadProposalFont, addFontFaceRule, preloadFont } from 'helpers/proposal';
import FontSelect from './FontSelect';
import SelectPopupContainer from 'components/SelectPopupContainer/SelectPopupContainer';

import './FontsMenu.scss';

const { Option } = Select;

const maxFontSizeMB = 1;
const maxFontSizeBytes = maxFontSizeMB * 1024 * 1024;

// store all font and their weights as key value pair for faster search
const fontFamilyWeightsMap = [...fontListEnglish, ...fontListHebrew].reduce((acc, f) => {
  acc[f.family] = f.weights;
  return acc;
}, {});

// get the weights for selected font
const getFontWeights = (family) => fontFamilyWeightsMap[family] || [400];

const getFont = (defaultSettings, draftFont = {}) => {
  draftFont.weights = getFontWeights(draftFont.family);

  const f = { ...defaultSettings, ...draftFont };
  // convert string weight to number
  if (typeof f?.weight === 'string') {
    f.weight = Number(f.weight) || 400;
  }

  if (f.weights && f.weights.indexOf(f.weight) === -1) {
    [f.weight] = f.weights;
  }

  return f;
};

const FontsMenu = ({ draft, user, isSection, sectionName }) => {
  const { proposal, wixEditor, saveTemplateProposal: saveProposal } = useProposalContext();
  const language = proposal?.language?.toLowerCase() || 'english';

  const inputFileRef = useRef(null);
  const fontLoaded = useReactiveVar(fontLoadedVar);
  const [upload] = Uploader();
  // these are the lastest value to be respected
  // TODO these can be stored in proposal level instead of just in the side font menu
  const [cache, setCache] = useState({ count: {} });

  const [titleFont, setTitleFont] = useState({ ...defaultTitleSettings });
  const [subTitleFont, setSubTitleFont] = useState({ ...defaultSubTitleSettings });
  const [bodyFont, setBodyFont] = useState({ ...defaultBodySettings });
  const [fontList, setFontList] = useState([]);
  const [customFontList, setCustomFontList] = useState([]);
  const [defaultFont, setDefaultFont] = useState();
  const [uploadCategory, setUploadCategory] = useState(null);
  const [uploadingFontCategory, setUploadingFontCategory] = useState(null);
  const [openFontSelects, setOpenFontSelects] = useState({
    title: false,
    subtitle: false,
    body: false,
  });

  const handlePrelistLoad = (langChange) => {
    let prelist = [];

    if (language?.toLowerCase() === 'hebrew' || langChange === 'hebrew') {
      prelist = fontListHebrew;
    } else {
      prelist = fontListEnglish;
    }

    if (!fontLoaded) {
      fontLoadedVar(true);
      loadProposalFont(prelist, langChange || language, user);
    }

    setFontList(prelist);
    const currentUser = userVar() || user;
    setCustomFontList(currentUser?.uploadedFonts || []);
    setDefaultFont(currentUser?.branding?.font || user?.branding?.font || 'Poppins');
  };

  const persistUploadedFonts = (uploadedFonts) => {
    const currentUser = userVar() || user;
    if (currentUser && typeof currentUser === 'object') {
      userVar({ ...currentUser, uploadedFonts });
    }
    setCustomFontList(uploadedFonts || []);
  };

  const [saveUserFont] = useMutation(USER_UPDATE, {
    onCompleted(data) {
      const uploadedFonts = data?.updateUser?.uploadedFonts;
      if (Array.isArray(uploadedFonts) && uploadedFonts.length) {
        persistUploadedFonts(uploadedFonts);
      }
      handlePrelistLoad(language?.toLowerCase());
      setUploadingFontCategory(null);
    },
    onError: ({ graphQLErrors }) => {
      setUploadingFontCategory(null);
      message.error(graphQLErrors[0].message);
    },
  });

  const handleEffect = (draftValue, key) => {
    // if draftValue is empty, return
    if (!draftValue) {
      return;
    }
    // if cache exists, return (those are lastest value in UI)
    if (cache?.[key]) {
      // if draftValue = cache
      if (_.isEqual(draftValue, cache[key])) {
        setCache((prev) => {
          const _cache = { ...prev };

          if (_cache.count?.[key] === 3) {
            // on 3rd hit remove cache
            delete _cache.count[key];
            delete _cache[key];
          } else {
            _cache.count[key] = (_cache.count[key] || 0) + 1;
          }

          return _cache;
        });
      }
      // updating values from draftValue might cause glitch in ui
      // as those are probably old values, wait untill cache is cleared
      return;
    }

    if (key === 'titleFont') {
      setTitleFont(getFont(defaultTitleSettings, draftValue));
    } else if (key === 'subTitleFont') {
      setSubTitleFont(getFont(defaultSubTitleSettings, draftValue));
    } else if (key === 'bodyFont') {
      setBodyFont(getFont(defaultBodySettings, draftValue));
    }
  };

  useEffect(() => {
    handlePrelistLoad(language?.toLowerCase());

    if (
      titleFont &&
      language?.toLowerCase() === 'hebrew' &&
      (titleFont.family === 'Sharpsansno1 book' || titleFont.family === 'Droid Sans')
    )
      setTitleFont(defaultHebrewTitleSettings);

    if (
      subTitleFont &&
      language?.toLowerCase() === 'hebrew' &&
      (subTitleFont.family === 'Sharpsansno1 book' || subTitleFont.family === 'Droid Sans')
    )
      setSubTitleFont(defaultHebrewBodySettings);

    if (
      bodyFont &&
      language?.toLowerCase() === 'hebrew' &&
      (bodyFont.family === 'Sharpsansno1 book' || bodyFont.family === 'Droid Sans')
    )
      setBodyFont(defaultHebrewBodySettings);
  }, []);

  const draftKey = isSection ? `draft.${sectionName}.sectionStyles` : `draft`;
  const sectionStyles = draft?.[sectionName]?.sectionStyles;
  const titleFontEffect = sectionStyles?.titleFont || draft?.titleFont;
  const subTitleFontEffect = sectionStyles?.subTitleFont || draft?.subTitleFont;
  const bodyFontEffect = sectionStyles?.bodyFont || draft?.bodyFont;

  useEffect(() => {
    handleEffect(titleFontEffect, 'titleFont');
  }, [titleFontEffect?.titleFont]);

  useEffect(() => {
    handleEffect(subTitleFontEffect, 'subTitleFont');
  }, [subTitleFontEffect?.subTitleFont]);

  useEffect(() => {
    handleEffect(bodyFontEffect, 'bodyFont');
  }, [bodyFontEffect?.bodyFont]);

  const uploadUserFont = (category) => {
    setUploadCategory(category);
    inputFileRef.current.click();
  };

  const validateFileSize = (file) => {
    if (file.size > maxFontSizeBytes) {
      message.error(`The file size exceeds the limit of ${maxFontSizeMB}MB.`);
      return false;
    }
    return true;
  };

  const handleFontUploadError = (error) => {
    setUploadingFontCategory(null);
    message.error(error.message);
  };

  const extractFontInfo = (font, filename, fontFormat) => {
    const fontFamily = font.names.fullName.en || filename.split('.')[0];
    const fontTitle = font.tables.name.postScriptName.en || filename.split('.')[0];
    const weight = font.tables.os2.usWeightClass?.toString() || '400';

    return {
      family: fontFamily,
      weights: [weight],
      source: 'custom',
      title: fontTitle,
      format: fontFormat,
    };
  };

  const getFontFormat = (filename) => {
    const extension = filename.split('.').pop().toLowerCase();
    switch (extension) {
      case 'ttf':
        return 'truetype';
      case 'otf':
        return 'opentype';
      case 'woff':
        return 'woff';
      default:
        return 'unknown';
    }
  };

  const uploadFont = async (file) => {
    const { url, filename } = await upload(file, `userfonts/${user._id}`);
    const arrayBuffer = await file.arrayBuffer();
    const font = opentype.parse(arrayBuffer);

    const fontFormat = getFontFormat(filename);

    return {
      fontInfo: extractFontInfo(font, filename, fontFormat),
      url,
    };
  };

  const onUserFontUpload = async ({ target }) => {
    const {
      validity,
      files: [file],
    } = target;
    if (!validity.valid || !validateFileSize(file)) return;

    Object.entries(openFontSelects).forEach(([category]) => {
      if (uploadCategory === category) {
        setUploadingFontCategory(uploadCategory);
      }
    });

    const existingFonts = userVar()?.uploadedFonts || user?.uploadedFonts || [];
    try {
      const { fontInfo, url } = await uploadFont(file);
      let newFont = { ...fontInfo, url };

      let fontExists = existingFonts.some((font) => font.family === newFont.family);
      let counter = 0;

      while (fontExists && counter < numberWords.length) {
        const suffix = ` ${numberWords[counter]}`;
        newFont.title = `${fontInfo.title}${suffix}`;
        newFont.family = `${fontInfo.family}${suffix}`;
        fontExists = existingFonts.some((font) => font.family === newFont.family);
        counter++;
      }

      const updatedFonts = [...existingFonts, newFont];
      // keep the in memory user in sync before the request returns
      persistUploadedFonts(updatedFonts);

      const result = await saveUserFont({
        variables: {
          user: {
            id: user?._id,
            uploadedFonts: updatedFonts,
          },
        },
      });

      const returnedFonts = result?.data?.updateUser?.uploadedFonts;
      const savedFonts =
        Array.isArray(returnedFonts) && returnedFonts.length ? returnedFonts : updatedFonts;
      persistUploadedFonts(savedFonts);

      const { family, url: fontUrl, weights, format } = newFont;
      preloadFont(fontUrl);
      addFontFaceRule(family, fontUrl, weights, format);
    } catch (error) {
      persistUploadedFonts(existingFonts);
      handleFontUploadError(error);
    } finally {
      target.value = '';
    }
  };

  const nearSimilarWeight = (weights, weight) => {
    // match close/similar weight if weight is not found in weights
    if (weight === 4000 || weights.includes(weight)) {
      return weight;
    }

    return weight < 500 ? weights[0] : nearSimilarWeight(weights, weight - 100);
  };

  const updateFont = (updateType, oldvalue, type, value) => {
    if (value === undefined) {
      value = '';
    }

    const newValue = { ...oldvalue, [type]: value };
    // On updating family, update weights also
    if (type === 'family') {
      newValue.weights = getFontWeights(value);
      if (newValue.weight) {
        newValue.weight = nearSimilarWeight(newValue.weights, newValue.weight);
      }
    }

    const saveData = { [`${draftKey}.${updateType}`]: newValue };
    // if (!isSection) {
    //   saveData['draft.sectionStyles'] = {};
    // }

    saveProposal(saveData);
    setCache((prev) => ({ ...prev, [updateType]: newValue }));
    return newValue;
  };

  const handleFontSave = (font, type, value) => {
    switch (font) {
      case 'title':
        setTitleFont((prev) => updateFont('titleFont', prev, type, value));
        return;
      case 'subtitle':
        setSubTitleFont((prev) => updateFont('subTitleFont', prev, type, value));
        return;
      case 'body':
        setBodyFont((prev) => updateFont('bodyFont', prev, type, value));
        return;
      default:
    }
  };

  const onReset = () => {
    let tempTitleFont, tempSubTitleFont, tempBodyFont;
    if (language?.toLowerCase() === 'hebrew') {
      tempTitleFont = defaultHebrewTitleSettings;
      tempSubTitleFont = defaultHebrewBodySettings;
      tempBodyFont = defaultHebrewBodySettings;
    } else {
      tempTitleFont = defaultTitleSettings;
      tempSubTitleFont = defaultSubTitleSettings;
      tempBodyFont = defaultBodySettings;
    }

    setTitleFont(tempTitleFont);
    setSubTitleFont(tempSubTitleFont);
    setBodyFont(tempBodyFont);

    setCache((prev) => ({
      ...prev,
      titleFont: tempTitleFont,
      subTitleFont: tempSubTitleFont,
      bodyFont: tempBodyFont,
    }));

    if (isSection) {
      saveProposal({
        [`${draftKey}`]: {
          ...sectionStyles,
          titleFont: tempTitleFont,
          subTitleFont: tempSubTitleFont,
          bodyFont: tempBodyFont,
        },
      });
    } else {
      saveProposal({
        'draft.titleFont': tempTitleFont,
        'draft.subTitleFont': tempSubTitleFont,
        'draft.bodyFont': tempBodyFont,
      });
    }
  };

  // call color change only once
  const debounceHandleFontSave = useRef(
    _.debounce((font, type, value) => {
      handleFontSave(font, type, value);
    }, 500)
  ).current;

  return (
    <Layout className="font-menu">
      <h3 className="font-menu-title">Titles</h3>
      <Form className="font-menu-form" layout="vertical">
        <Row>
          <Col xs={24}>
            <FontSelect
              id="rhs-title-font-select"
              fontLists={fontList}
              customFonts={customFontList}
              defaultFont={defaultFont}
              font={titleFont}
              onChange={(value) => {
                handleFontSave('title', 'family', value);
              }}
              className={uploadingFontCategory !== 'title' ? 'select-custom-suffix' : ''}
              disabled={uploadingFontCategory === 'title'}
              loading={uploadingFontCategory === 'title'}
              open={openFontSelects.title}
              onDropdownVisibleChange={(visible) =>
                setOpenFontSelects((prev) => ({ ...prev, title: visible }))
              }
              dropdownRender={(menu) => (
                <div className="font-select-with-upload">
                  {menu}
                  <div className="font-upload-option" onClick={() => uploadUserFont('title')}>
                    <UploadFontIcon /> <span>Upload Font</span>
                    <Tooltip
                      overlayClassName="wizard-tooltip-email settings-tip"
                      title="Upload custom fonts in these formats: .ttf, .otf, .woff"
                      color="black"
                      key="white">
                      <UploadInfoIcon className="info-icon upload-info-icon" />
                    </Tooltip>
                  </div>
                </div>
              )}
            />
          </Col>
        </Row>
        <Row gutter={12}>
          <Col xs={12}>
            <SelectPopupContainer
              label="Style"
              id="rhs-title-weight-select"
              placeholder="Extra Bold"
              value={titleFont.weight}
              onChange={(value) => handleFontSave('title', 'weight', value)}>
              {titleFont.weights.map((item) => (
                <Option key={item} value={item} className="">
                  {item}
                </Option>
              ))}
            </SelectPopupContainer>
          </Col>
          <Col xs={12}>
            <SelectPopupContainer
              label="Font Size"
              id="rhs-title-size-select"
              placeholder="24px"
              value={titleFont.fontSize}
              onChange={(value) => handleFontSave('title', 'fontSize', value)}>
              {fontSizes.map((item) => (
                <Option key={item} className="">
                  {item}
                </Option>
              ))}
            </SelectPopupContainer>
          </Col>
          <Col xs={12} className="color-box-col">
            <Form.Item label="Header Primary Color" colon={false}>
              <ColorBox
                color={titleFont.color1}
                onChange={(value) => debounceHandleFontSave('title', 'color1', value)}
              />
            </Form.Item>
          </Col>
          <Col xs={12} className="color-box-col">
            <Form.Item label="Section Title Color" colon={false}>
              <ColorBox
                color={titleFont.color3}
                onChange={(value) => debounceHandleFontSave('title', 'color3', value)}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col className="color-box-col">
            <Form.Item label="Header Secondary color" className="no-bottom-margin" colon={false}>
              <ColorBox
                color={titleFont.color2}
                onChange={(value) => debounceHandleFontSave('title', 'color2', value)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <h3 className="font-menu-title">Subtitles</h3>
      <Form className="font-menu-form" layout="vertical">
        <Row>
          <Col xs={24}>
            <FontSelect
              id="rhs-subtitle-font-select"
              fontLists={fontList}
              customFonts={customFontList}
              defaultFont={defaultFont}
              font={subTitleFont}
              onChange={(value) => {
                handleFontSave('subtitle', 'family', value);
              }}
              className={uploadingFontCategory !== 'subtitle' ? 'select-custom-suffix' : ''}
              disabled={uploadingFontCategory === 'subtitle'}
              loading={uploadingFontCategory === 'subtitle'}
              open={openFontSelects.subtitle}
              onDropdownVisibleChange={(visible) =>
                setOpenFontSelects((prev) => ({ ...prev, subtitle: visible }))
              }
              dropdownRender={(menu) => (
                <div className="font-select-with-upload">
                  {menu}
                  <div className="font-upload-option" onClick={() => uploadUserFont('subtitle')}>
                    <UploadFontIcon /> <span>Upload Font</span>
                    <Tooltip
                      overlayClassName="wizard-tooltip-email settings-tip"
                      title="Upload custom fonts in these formats: .ttf, .otf, .woff"
                      color="black"
                      key="white">
                      <UploadInfoIcon className="info-icon upload-info-icon" />
                    </Tooltip>
                  </div>
                </div>
              )}
            />
          </Col>
        </Row>
        <Row gutter={12}>
          <Col xs={12}>
            <SelectPopupContainer
              label="Style"
              id="rhs-subtitle-weight-select"
              placeholder="Extra Bold"
              value={subTitleFont.weight}
              onChange={(value) => handleFontSave('subtitle', 'weight', value)}>
              {subTitleFont.weights.map((item) => (
                <Option key={item} className="">
                  {item}
                </Option>
              ))}
            </SelectPopupContainer>
          </Col>
          <Col xs={12}>
            <SelectPopupContainer
              label="Font Size"
              id="rhs-subtitle-size-select"
              placeholder="24px"
              value={subTitleFont.fontSize}
              onChange={(value) => handleFontSave('subtitle', 'fontSize', value)}>
              {fontSizes.map((item) => (
                <Option key={item} className="">
                  {item}
                </Option>
              ))}
            </SelectPopupContainer>
          </Col>
        </Row>
        <Row>
          <Col className="color-box-col">
            <Form.Item label="Subtitle Color" className="no-bottom-margin" colon={false}>
              <ColorBox
                color={subTitleFont.color1}
                onChange={(value) => debounceHandleFontSave('subtitle', 'color1', value)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <h3 className="font-menu-title">Body Text</h3>
      <Form className="font-menu-form" layout="vertical">
        <Row>
          <Col xs={24}>
            <FontSelect
              id="rhs-body-font-select"
              fontLists={fontList}
              customFonts={customFontList}
              defaultFont={defaultFont}
              font={bodyFont}
              onChange={(value) => {
                handleFontSave('body', 'family', value);
              }}
              className={uploadingFontCategory !== 'body' ? 'select-custom-suffix' : ''}
              disabled={uploadingFontCategory === 'body'}
              loading={uploadingFontCategory === 'body'}
              open={openFontSelects.body}
              onDropdownVisibleChange={(visible) =>
                setOpenFontSelects((prev) => ({ ...prev, body: visible }))
              }
              dropdownRender={(menu) => (
                <div className="font-select-with-upload">
                  {menu}
                  <div className="font-upload-option" onClick={() => uploadUserFont('body')}>
                    <UploadFontIcon /> <span>Upload Font</span>
                    <Tooltip
                      overlayClassName="wizard-tooltip-email settings-tip"
                      title="Upload custom fonts in these formats: .ttf, .otf, .woff"
                      color="black"
                      key="white">
                      <UploadInfoIcon className="info-icon upload-info-icon" />
                    </Tooltip>
                  </div>
                </div>
              )}
            />
          </Col>
        </Row>
        <Row gutter={12}>
          <Col xs={12}>
            <SelectPopupContainer
              label="Style"
              id="rhs-body-weight-select"
              placeholder="Extra Bold"
              value={bodyFont.weight}
              onChange={(value) => handleFontSave('body', 'weight', value)}>
              {bodyFont.weights.map((item) => (
                <Option key={item} className="">
                  {item}
                </Option>
              ))}
            </SelectPopupContainer>
          </Col>
          <Col xs={12}>
            <SelectPopupContainer
              label="Font Size"
              id="rhs-body-size-select"
              placeholder="24px"
              value={bodyFont.fontSize}
              onChange={(value) => handleFontSave('body', 'fontSize', value)}>
              {
                // render till 60px
                fontSizes.slice(0, wixEditor ? -4 : -2).map((item) => (
                  <Option key={item} className="">
                    {item}
                  </Option>
                ))
              }
            </SelectPopupContainer>
          </Col>
          <Col xs={12} className="color-box-col">
            <Form.Item label="Header Primary Color" className="no-bottom-margin" colon={false}>
              <ColorBox
                color={bodyFont.color1}
                onChange={(value) => debounceHandleFontSave('body', 'color1', value)}
              />
            </Form.Item>
          </Col>
          <Col xs={12} className="color-box-col">
            <Form.Item label="Proposal Text Color" className="no-bottom-margin" colon={false}>
              <ColorBox
                color={bodyFont.color2}
                onChange={(value) => debounceHandleFontSave('body', 'color2', value)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <ResetItem onReset={onReset} />
      <input
        type="file"
        className="file_bag"
        accept=".ttf,.woff,.otf"
        ref={inputFileRef}
        onChange={onUserFontUpload}
        style={{ display: 'none', position: 'absolute', left: -10000 }}
      />
    </Layout>
  );
};

FontsMenu.propTypes = {
  draft: PropTypes.instanceOf(Object).isRequired,
  user: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.string]),
  sectionName: PropTypes.string,
  isSection: PropTypes.bool,
};

export default FontsMenu;
